// HitterReportsPage.jsx - Professional Scouting Reports
import React, { useMemo, useState } from 'react';
import { MOCK_TO_REAL_HITTER_MAPPING } from '../utils/mockPlayerNames';
import { loadAllHittingLogs } from '../utils/hittingLogsAdapter';
import CustomDropdown from '../components/ui/CustomDropdown';
import { calculateDraftLeaguePerformance, getPerformanceColor, getPerformanceBackground } from '../utils/draftLeagueBaselines';
import { DL_BASELINE } from '../constants/draftLeague.js';
import { 
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ReferenceLine
} from 'recharts';

// Thunder-styled components
const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-slate-800 text-white shadow-xl ${className}`}>
    {children}
  </div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-4 ${className}`}>
    {children}
  </div>
);

// Professional MLB Grade Badge Component
const GradeBadge = ({ grade, label }) => {
  const getGradeInfo = (grade) => {
    if (grade >= 70) return { color: 'bg-red-600', text: 'text-white', desc: 'Plus' };
    if (grade >= 60) return { color: 'bg-orange-500', text: 'text-white', desc: 'Above Avg' };
    if (grade >= 55) return { color: 'bg-yellow-500', text: 'text-black', desc: 'Avg+' };
    if (grade >= 50) return { color: 'bg-green-600', text: 'text-white', desc: 'Average' };
    if (grade >= 45) return { color: 'bg-blue-600', text: 'text-white', desc: 'Below Avg' };
    return { color: 'bg-gray-600', text: 'text-white', desc: 'Poor' };
  };

  const gradeInfo = getGradeInfo(grade);

  return (
    <div className="text-center">
      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full font-bold text-xl border-2 border-white/20 shadow-lg ${gradeInfo.color} ${gradeInfo.text}`}>
        {grade}
      </div>
      <div className="text-xs text-white/70 mt-2 font-medium">{label}</div>
      <div className="text-xs text-white/50 mt-1">{gradeInfo.desc}</div>
    </div>
  );
};

// Professional Combined Scouting Report with Amateur Scout Tools
function ProfessionalScoutingReport({ data, hitter, profile, performanceData }) {
  // Amateur scouting tools for Draft League evaluation
  const scoutingTools = [
    { name: 'Hit Tool', grade: data.hit, desc: 'Contact ability and bat-to-ball skills' },
    { name: 'Raw Power', grade: data.power, desc: 'Home run potential and exit velocity' },
    { name: 'Speed', grade: data.speed, desc: 'Baserunning and stolen base ability' },
    { name: 'Arm Strength', grade: data.arm, desc: 'Throwing velocity and accuracy' },
    { name: 'Fielding', grade: data.field, desc: 'Defensive range and glove work' }
  ];

  const overallGrade = profile.overallFV || Math.round((data.hit + data.power + data.speed + data.arm + data.field) / 5);
  
  const getScoutingReport = (hitter, grades, profile) => {
    const reports = {
      'Jake Allen': {
        summary: profile.summary || 'Contact-oriented outfielder with compact swing and athletic actions. Shows ability to work counts and spray the ball to all fields. Below-average raw power limits slug ceiling, but consistent bat-to-ball skills give him a chance to carve out a role. Defense is steady enough for corner OF, and running ability plays up on the bases.',
        strengths: profile.keyStrengths ? profile.keyStrengths.split('; ') : ['Advanced feel for contact', 'above-average runner'],
        areas: profile.mainConcerns ? profile.mainConcerns.split('; ') : ['Limited raw power', 'arm may restrict him to LF/CF depth'],
        projection: profile.developmentFocus || 'Add strength to drive gaps more consistently'
      },
      'Tyler Armstrong': {
        summary: profile.summary || 'Left-handed bat with a line-drive approach and steady zone awareness. Doesn\'t expand much but lacks big impact off the barrel. Profiles more as a high-contact, low-strikeout type with defensive versatility at 1B and corner OF. Development tied to whether he can elevate and tap into more extra-base power.',
        strengths: profile.keyStrengths ? profile.keyStrengths.split('; ') : ['Bat-to-ball skills', 'consistent approach'],
        areas: profile.mainConcerns ? profile.mainConcerns.split('; ') : ['Light power', 'fringy arm strength'],
        projection: profile.developmentFocus || 'Emphasize loft and hard contact to expand offensive value'
      },
      'Alex Bell': {
        summary: profile.summary || 'Corner bat with flashes of raw power but uneven contact quality. When synced up, produces carry to the pull side. Defense is passable at 3B/1B with an average arm, though limited lateral quickness likely pushes him to 1B long-term. Needs to refine approach and cut down chase to unlock offensive ceiling.',
        strengths: profile.keyStrengths ? profile.keyStrengths.split('; ') : ['Raw strength', 'playable arm for 3B'],
        areas: profile.mainConcerns ? profile.mainConcerns.split('; ') : ['Streaky contact', 'below-average running ability'],
        projection: profile.developmentFocus || 'Improve swing decisions; convert raw pop into game power'
      },
      'Michael Butler': {
        summary: profile.summary || 'Utility-type bat without a carrying tool. Profiles as a role player who can grind at-bats but lacks impact offensively or defensively. Solid competitor who brings energy, but tools project as below-average across the board.',
        strengths: profile.keyStrengths ? profile.keyStrengths.split('; ') : ['Hard-nosed', 'competitive at-bats'],
        areas: profile.mainConcerns ? profile.mainConcerns.split('; ') : ['No plus tool', 'limited offensive upside'],
        projection: profile.developmentFocus || 'Emphasize versatility, polish approach for depth role'
      },
      'David Campbell': {
        summary: profile.summary || 'Balanced offensive profile with flashes of gap power. Shows strength in swing, though hit tool is raw and prone to swing-and-miss against quality pitching. Projects as a corner bat with moderate upside if he develops more consistent barrel accuracy.',
        strengths: profile.keyStrengths ? profile.keyStrengths.split('; ') : ['Strong build', 'flashes of raw pop'],
        areas: profile.mainConcerns ? profile.mainConcerns.split('; ') : ['Swing length', 'inconsistent contact'],
        projection: profile.developmentFocus || 'Improve barrel control, simplify swing mechanics'
      },
      'Chris Anderson': {
        summary: 'Division I outfielder with fringe tools across the board. Athletic frame but lacks standout tools.',
        strengths: ['Athletic frame', 'Barrel control flashes', 'Coachable makeup'],
        areas: ['Lacks present power', 'Fringe arm for OF', 'Below-average hit projection'],
        projection: 'Fourth outfielder ceiling. Organizational depth piece. Late-round filler pick.'
      },
      'Sam Wilson': {
        summary: 'Junior college utility player with below-average tools. Limited upside but versatile defender.',
        strengths: ['Defensive versatility', 'Competitive makeup', 'Above-average speed'],
        areas: ['Well below-average hit tool', 'Limited power potential', 'Fringe defensive skills'],
        projection: 'Organizational depth only. Utility role at best. UDFA candidate.'
      },
      'Kevin Martinez': {
        summary: 'Division II catcher with fringe tools but solid game awareness. Limited offensive upside but reliable receiver.',
        strengths: ['Solid receiver', 'Good pitcher relationships', 'Raw power flashes'],
        areas: ['Below-average hit projection', 'Limited power potential', 'Fringe arm strength'],
        projection: 'Backup catcher ceiling. Organizational depth piece. Late-round developmental pick.'
      },
      'Luis Garcia': {
        summary: 'Community college middle infielder with contact-over-power profile. Barrel control flashes but lacks impact tools.',
        strengths: ['Consistent contact', 'Solid infield actions', 'Good baseball IQ'],
        areas: ['Well below-average power', 'Limited athletic ability', 'Fringe arm for SS'],
        projection: 'Utility infielder ceiling. Organizational filler. Late-round depth pick.'
      }
    };
    
    // Generate generic amateur scouting report based on grades
    const getGenericReport = (grades, profile) => {
      const overallGrade = (grades.hit + grades.power + grades.speed + grades.arm + grades.field) / 5;
      
      if (overallGrade >= 45) {
        return {
          summary: `${profile.college} ${profile.position} with solid tools and good development potential. Shows multiple above-average tools with room for growth.`,
          strengths: ['Solid athletic ability', 'Good baseball instincts', 'Coachable with work ethic'],
          areas: ['Continue strength development', 'Refine approach vs advanced pitching', 'Add consistency'],
          projection: `Everyday player potential. ${profile.draftRound} round pick with solid floor and ceiling.`
        };
      } else if (overallGrade >= 40) {
        return {
          summary: `${profile.college} ${profile.position} with average tools and development potential. Shows solid foundation with room for improvement.`,
          strengths: ['Decent athleticism', 'Coachable demeanor', 'Good work ethic'],
          areas: ['Continue tool development', 'Add strength and consistency', 'Improve against advanced pitching'],
          projection: `Role player potential. ${profile.draftRound} round consideration with development upside.`
        };
      } else {
        return {
          summary: `${profile.college} ${profile.position} with fringe tools but shows effort and coachability. Limited present impact but developmental potential.`,
          strengths: ['Good work ethic', 'Coachable player', 'Team-first attitude'],
          areas: ['Needs significant development', 'Limited present tools', 'Must add strength and consistency'],
          projection: `Organizational depth. Late-round developmental pick with long-term upside.`
        };
      }
    };
    
    return reports[hitter] || getGenericReport(grades, profile);
  };

  const report = getScoutingReport(hitter, data, profile);

  return (
    <Card>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Scouting Grades */}
          <div className="space-y-2">
            <h4 className="text-base font-semibold text-white">Tool Grades</h4>
            <div className="space-y-1.5">
              {scoutingTools.map((tool, index) => (
                <div key={index} className="flex items-center justify-between p-1.5 bg-slate-700/30 rounded-lg">
                  <div className="flex-1">
                    <div className="text-base font-medium text-white">{tool.name}</div>
                    <div className="text-sm text-white/60">{tool.desc}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      tool.grade >= 60 ? 'bg-orange-500 text-white' :
                      tool.grade >= 55 ? 'bg-yellow-500 text-black' :
                      tool.grade >= 50 ? 'bg-green-600 text-white' :
                      tool.grade >= 45 ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white'
                    }`}>
                      {tool.grade}
                    </div>
                    <div className="text-sm text-white/70 w-16">
                      {tool.grade >= 60 ? 'Above Avg' :
                       tool.grade >= 55 ? 'Avg+' :
                       tool.grade >= 50 ? 'Average' :
                       tool.grade >= 45 ? 'Below Avg' : 'Poor'}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-slate-700/50 rounded-lg p-4 border-l-4 border-yellow-400">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-white">Overall Future Value (OFV)</div>
                  <div className="text-xs text-white/60">Amateur prospect grade • {profile.draftRound} Round</div>
                </div>
                <div className="text-2xl font-bold text-yellow-400">{overallGrade}</div>
              </div>
              <div className="mt-2 text-xs text-white/50">
                {overallGrade >= 40 ? 'Fringe prospect with development potential' : 
                 overallGrade >= 35 ? 'Organizational depth piece' : 
                 'Limited upside - UDFA candidate'}
              </div>
            </div>
          </div>

          {/* Scouting Report */}
          <div className="space-y-2">
            <h4 className="text-base font-semibold text-white">Scout's Analysis</h4>
            
            <div className="bg-slate-700/30 rounded-lg p-2">
              <h5 className="text-sm font-semibold text-blue-400 mb-2">Notes</h5>
              <p className="text-sm text-white/80 leading-relaxed">{report.summary}</p>
            </div>

            <div className="bg-slate-700/30 rounded-lg p-2">
              <h5 className="text-sm font-semibold text-green-400 mb-2">Strengths</h5>
              <ul className="text-sm text-white/80 space-y-1">
                {report.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">•</span>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-700/30 rounded-lg p-2">
              <h5 className="text-sm font-semibold text-orange-400 mb-2">Development Areas</h5>
              <ul className="text-sm text-white/80 space-y-1">
                {report.areas.map((area, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-orange-400 mt-1">•</span>
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-3 pt-2 border-t border-white/10">
                <h6 className="text-sm font-semibold text-purple-400 mb-1">Projection</h6>
                <p className="text-sm text-white/80">{report.projection}</p>
              </div>
            </div>
          </div>

          {/* Performance Metrics */}
          <div className="space-y-2">
            <h4 className="text-base font-semibold text-white">Performance Metrics</h4>
            <p className="text-xs text-white/70">{hitter} vs Draft League averages</p>
            
            <div className="grid grid-cols-2 gap-2">
              {performanceData && [
                { metric: 'Exit Velo', value: performanceData.avgExitVelo, dlAvg: DL_BASELINE.ev, color: '#F59E0B' },
                { metric: 'Hard Hit', value: performanceData.hardHitRate, dlAvg: DL_BASELINE.hardHitPct, color: '#10B981' },
                { metric: 'Barrel Rate', value: performanceData.barrelRate, dlAvg: DL_BASELINE.barrelPct, color: '#8B5CF6' },
                { metric: 'Sweet Spot', value: performanceData.sweetSpotRate, dlAvg: DL_BASELINE.sweetSpotPct, color: '#F97316' },
                { metric: 'Max Velo', value: performanceData.maxExitVelo, dlAvg: DL_BASELINE.maxEV, color: '#EF4444' }
              ].slice(0, 5).map((metric, index) => (
                <div key={index} className="bg-slate-700/50 rounded-lg p-2 text-center">
                  <div className="text-base font-bold text-yellow-400">{metric.value.toFixed(1)}</div>
                  <div className="text-xs text-white/70">{metric.metric}</div>
                  <div className="text-xs text-white/50">DL: {metric.dlAvg}</div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Performance Metrics Chart
function PerformanceMetrics({ data, hitter }) {
  const metricsData = [
    { metric: 'Exit Velocity', value: data.avgExitVelo, dlAvg: DL_BASELINE.ev, color: '#F59E0B' },
    { metric: 'Hard Hit Rate', value: data.hardHitRate, dlAvg: DL_BASELINE.hardHitPct, color: '#10B981' },
    { metric: 'Barrel Rate', value: data.barrelRate, dlAvg: DL_BASELINE.barrelPct, color: '#3B82F6' },
    { metric: 'Sweet Spot %', value: data.sweetSpotRate, dlAvg: DL_BASELINE.sweetSpotPct, color: '#8B5CF6' },
    { metric: 'Max Exit Velo', value: data.maxExitVelo, dlAvg: DL_BASELINE.maxEV, color: '#EF4444' }
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-900 border border-yellow-400/30 rounded-lg p-3 shadow-xl">
          <p className="text-yellow-400 font-semibold">{data.metric}</p>
          <p className="text-white text-sm">Player: {data.value.toFixed(1)}</p>
          <p className="text-white/70 text-sm">DL Avg: {data.dlAvg.toFixed(1)}</p>
        </div>
      );
    }
    return null;
  };

  return null;
}

// Scouting Report Text
function ScoutingReport({ data, hitter }) {
  return (
    <Card>
      <CardContent>
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-white mb-2">Scouting Report</h3>
          <p className="text-sm text-white/70">Professional evaluation and development notes</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-400 mb-2">Strengths</h4>
              <ul className="text-sm text-white/80 space-y-1">
                {data.avgExitVelo >= 90 && <li>• Elite exit velocity production</li>}
                {data.hardHitRate >= 40 && <li>• Consistent hard contact ability</li>}
                {data.barrelRate >= 8 && <li>• Above-average barrel production</li>}
                {data.sweetSpotRate >= 35 && <li>• Excellent launch angle control</li>}
                <li>• Shows advanced plate discipline</li>
                <li>• Demonstrates power potential to all fields</li>
              </ul>
            </div>

            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-400 mb-2">Areas for Improvement</h4>
              <ul className="text-sm text-white/80 space-y-1">
                {data.avgExitVelo < 87 && <li>• Focus on increasing exit velocity through strength training</li>}
                {data.hardHitRate < 35 && <li>• Work on consistent barrel contact</li>}
                {data.barrelRate < 6 && <li>• Improve launch angle optimization</li>}
                <li>• Continue developing two-strike approach</li>
                <li>• Refine swing path for better contact consistency</li>
              </ul>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-400 mb-2">Development Plan</h4>
              <div className="text-sm text-white/80 space-y-2">
                <p><strong>Short-term (30 days):</strong> Focus on swing mechanics and launch angle consistency in batting practice.</p>
                <p><strong>Medium-term (90 days):</strong> Implement strength training program to increase exit velocity production.</p>
                <p><strong>Long-term (Season):</strong> Develop advanced situational hitting approach and two-strike adjustments.</p>
              </div>
            </div>

            <div className="bg-slate-700/50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-yellow-400 mb-2">MLB Comparison</h4>
              <div className="text-sm text-white/80">
                <p>Hitting profile shows similarities to contact-first hitters with developing power. 
                Exit velocity metrics suggest {data.avgExitVelo >= 90 ? 'elite' : data.avgExitVelo >= 87 ? 'above-average' : 'developing'} raw power potential.</p>
                <p className="mt-2">Overall projection: {
                  Math.round((data.hit + data.power) / 2) >= 60 ? 'Everyday player upside' :
                  Math.round((data.hit + data.power) / 2) >= 50 ? 'Solid contributor potential' :
                  'Developing prospect'
                }</p>
              </div>
            </div>

            <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
              <div className="text-xs text-blue-400 font-semibold mb-1">Scout's Summary</div>
              <div className="text-xs text-white/80">
                {hitter} demonstrates {data.avgExitVelo >= 90 ? 'advanced' : 'solid'} offensive tools with room for continued development. 
                Focus areas include launch angle optimization and situational hitting approach.
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function HitterReportsPage() {
  const [selectedHitter, setSelectedHitter] = useState('Jake Allen');

  const mockHitters = Object.keys(MOCK_TO_REAL_HITTER_MAPPING);

  // Load real hitting data
  const realHittingData = useMemo(() => loadAllHittingLogs(), []);

  // AMATEUR SCOUTING DATA - MLB Draft League Prospects (Yankees Scout Evaluations)
  const hitterData = useMemo(() => {
    const getMockHitterProfile = (hitterName) => {
      const profiles = {
        'Carlos Cooper': {
          scoutingGrades: { hit: 40, power: 35, speed: 50, arm: 45, field: 45 },
          performanceMetrics: { avgExitVelo: 82.1, hardHitRate: 29.8, barrelRate: 5.4, sweetSpotRate: 32.1, maxExitVelo: 99.5 },
          draftRound: '15-25', college: 'State University', position: 'INF',
          overallFV: 35,
          ageSize: '21 • 6\'1" • 190 lbs • R/R',
          stats: '.276 AVG | .341 OBP | .385 SLG | 6 SB',
          summary: 'Steady middle infielder with good contact skills and solid approach. Not a flashy profile, but consistent at-bats and reliable defense. Gaps to all fields, though below-average raw power limits offensive ceiling.',
          keyStrengths: 'Reliable defender; contact-oriented bat',
          mainConcerns: 'Limited impact power; average overall athleticism',
          developmentFocus: 'Continue improving bat strength to produce harder contact'
        },
        'Ryan Dawson': {
          scoutingGrades: { hit: 35, power: 35, speed: 45, arm: 50, field: 40 },
          performanceMetrics: { avgExitVelo: 80.8, hardHitRate: 26.4, barrelRate: 4.1, sweetSpotRate: 29.8, maxExitVelo: 97.2 },
          draftRound: '25-30/UDFA', college: 'Junior College', position: 'INF',
          overallFV: 30,
          ageSize: '22 • 6\'2" • 200 lbs • R/R',
          stats: '.242 AVG | .310 OBP | .360 SLG | 3 SB',
          summary: 'Large-framed player with well below-average offensive tools. Struggles to make consistent contact and lacks the bat speed to catch up to quality velocity. Age and limited skill set make him a poor draft investment.',
          keyStrengths: 'Size and arm strength',
          mainConcerns: 'Poor hit tool; swing and miss; age (22); limited upside',
          developmentFocus: 'Organizational filler. Unlikely professional future.'
        },
        'Josh Dixon': {
          scoutingGrades: { hit: 40, power: 30, speed: 55, arm: 45, field: 45 },
          performanceMetrics: { avgExitVelo: 81.9, hardHitRate: 28.7, barrelRate: 4.8, sweetSpotRate: 33.4, maxExitVelo: 98.6 },
          draftRound: '18-30', college: 'Community College', position: 'OF',
          overallFV: 35,
          ageSize: '20 • 5\'11" • 185 lbs • R/R',
          stats: '.268 AVG | .333 OBP | .375 SLG | 5 SB',
          summary: 'Quick-twitch OF who brings athleticism and speed to the roster. Pressure player on the bases with gap contact ability. Needs strength gains to avoid soft contact but projects as a table-setting OF with range.',
          keyStrengths: 'Speed and athleticism; outfield range',
          mainConcerns: 'Limited raw power; contact quality vs. velo',
          developmentFocus: 'Strength gains to boost gap production'
        },
        'Matt Edwards': {
          scoutingGrades: { hit: 45, power: 35, speed: 55, arm: 45, field: 50 },
          performanceMetrics: { avgExitVelo: 84.8, hardHitRate: 33.2, barrelRate: 7.1, sweetSpotRate: 34.8, maxExitVelo: 102.4 },
          draftRound: '12-20', college: 'Division II', position: 'INF',
          overallFV: 40,
          ageSize: '21 • 6\'0" • 188 lbs • S/R',
          stats: '.284 AVG | .361 OBP | .400 SLG | 7 SB',
          summary: 'Switch-hitter with steady bat control from both sides and advanced defensive feel. Brings a high-floor utility INF profile with the upside to carve out more if bat consistency continues. Shows base-stealing instincts and versatility that coaches value.',
          keyStrengths: 'Switch-hit contact ability; above-average defender',
          mainConcerns: 'Modest power impact; left-side swing still raw',
          developmentFocus: 'Build strength to add gap power; repeatable LH mechanics'
        },
        'Tony Escobar': {
          scoutingGrades: { hit: 35, power: 35, speed: 40, arm: 45, field: 40 },
          performanceMetrics: { avgExitVelo: 80.2, hardHitRate: 25.8, barrelRate: 3.9, sweetSpotRate: 28.4, maxExitVelo: 96.8 },
          draftRound: 'UDFA', college: 'NAIA School', position: 'OF',
          overallFV: 30,
          ageSize: '22 • 6\'1" • 200 lbs • R/R',
          stats: '.238 AVG | .308 OBP | .355 SLG | 2 SB',
          summary: 'Fringe prospect with well below-average tools across the board. Lacks the bat speed and athleticism to project as a professional player. Age (22) and limited ceiling make him an organizational filler at best.',
          keyStrengths: 'Effort and makeup',
          mainConcerns: 'Below-average hit tool; no power; limited athleticism; age',
          developmentFocus: 'Long-shot organizational depth. Unlikely to advance beyond rookie ball.'
        },
        'Steve Fisher': {
          scoutingGrades: { hit: 35, power: 30, speed: 45, arm: 40, field: 40 },
          performanceMetrics: { avgExitVelo: 79.8, hardHitRate: 24.1, barrelRate: 3.4, sweetSpotRate: 31.2, maxExitVelo: 95.4 },
          draftRound: '25-30/UDFA', college: 'Division III', position: 'OF',
          overallFV: 35,
          ageSize: '21 • 5\'10" • 180 lbs • L/R',
          stats: '.271 AVG | .345 OBP | .377 SLG | 4 SB',
          summary: 'Division III OF with average tools and development potential. Shows solid foundation with room for improvement.',
          keyStrengths: 'Decent athleticism; Coachable demeanor; Good work ethic',
          mainConcerns: 'Continue tool development; Add strength and consistency; Improve against advanced pitching',
          developmentFocus: 'Role player potential. 15-20 round consideration with development upside.'
        },
        'Nick Fox': {
          scoutingGrades: { hit: 55, power: 50, speed: 50, arm: 45, field: 45 },
          performanceMetrics: { avgExitVelo: 90.2, hardHitRate: 44.8, barrelRate: 22.4, sweetSpotRate: 48.6, maxExitVelo: 107.3 },
          draftRound: '4-8', college: 'Division II', position: 'INF',
          overallFV: 50,
          ageSize: '20 • 6\'1" • 185 lbs • R/R',
          stats: '.289 AVG | .357 OBP | .398 SLG | 1 HR | 5 SB',
          summary: 'Athletic infielder with balanced tools across the board. Quick bat speed and improving barrel control allow him to project as an above-average contributor at the plate. Defense is reliable with potential to stick in the middle infield.',
          keyStrengths: 'Balanced athletic profile; improving offensive consistency',
          mainConcerns: 'Needs to prove vs. higher velocity; still developing strength',
          developmentFocus: 'Refine approach against quality arms; build physicality'
        },
        'Brad Gibson': {
          scoutingGrades: { hit: 35, power: 35, speed: 40, arm: 40, field: 40 },
          performanceMetrics: { avgExitVelo: 79.2, hardHitRate: 24.8, barrelRate: 3.1, sweetSpotRate: 28.6, maxExitVelo: 96.4 },
          draftRound: 'UDFA', college: 'NAIA School', position: 'INF',
          overallFV: 30,
          ageSize: '22 • 6\'0" • 190 lbs • R/R',
          stats: '.244 AVG | .312 OBP | .355 SLG | 2 SB',
          summary: 'Fringe prospect with well below-average tools across the board. Lacks the bat speed and athleticism to project as a professional player. Age (22) and limited ceiling make him an organizational filler at best.',
          keyStrengths: 'Effort and makeup',
          mainConcerns: 'Below-average hit tool; no power; limited athleticism; age',
          developmentFocus: 'Long-shot organizational depth. Unlikely to advance beyond rookie ball.'
        },
        'Jake Allen': {
          scoutingGrades: { hit: 50, power: 45, speed: 55, arm: 45, field: 45 },
          performanceMetrics: { avgExitVelo: 92.3, hardHitRate: 42.1, barrelRate: 16.8, sweetSpotRate: 41.2, maxExitVelo: 105.7 },
          draftRound: '6-10', college: 'State University', position: 'OF',
          overallFV: 50,
          ageSize: '21 • 6\'0" • 185 lbs • L/R',
          stats: '.298 AVG | .368 OBP | .392 SLG | 1 HR | 7 SB',
          summary: 'Contact-oriented OF with compact swing and advanced feel for the barrel. Profiles as a top-of-order bat who can get on base and pressure defenses with speed. Defense is solid in LF/CF, though arm strength is just playable.',
          keyStrengths: 'High-contact profile; plus speed for the level',
          mainConcerns: 'Modest power ceiling; limited arm utility',
          developmentFocus: 'Add strength to sustain higher-level contact quality'
        },
        'Tyler Armstrong': {
          scoutingGrades: { hit: 45, power: 40, speed: 45, arm: 40, field: 45 },
          performanceMetrics: { avgExitVelo: 85.6, hardHitRate: 34.2, barrelRate: 11.8, sweetSpotRate: 33.4, maxExitVelo: 108.7 },
          draftRound: '8-12', college: 'Junior College', position: 'OF',
          overallFV: 45,
          ageSize: '21 • 5\'11" • 190 lbs • L/L',
          stats: '.274 AVG | .341 OBP | .372 SLG | 0 HR | 4 SB',
          summary: 'Advanced left-handed hitter with exceptional bat-to-ball skills and mature approach. One of the top contact hitters in the class with ability to work counts and spray line drives. Projects as a high-average, low-strikeout bat with enough pop to contribute offensively. Defensive versatility adds value.',
          keyStrengths: 'Elite bat-to-ball skills; advanced plate discipline; high baseball IQ',
          mainConcerns: 'Power development needed for higher levels',
          developmentFocus: 'Add strength and loft to unlock gap power potential'
        },
        'Alex Bell': {
          scoutingGrades: { hit: 35, power: 40, speed: 40, arm: 50, field: 45 },
          performanceMetrics: { avgExitVelo: 78.9, hardHitRate: 25.1, barrelRate: 3.8, sweetSpotRate: 35.4, maxExitVelo: 95.7 },
          draftRound: '15-25', college: 'Community College', position: 'INF',
          overallFV: 35,
          ageSize: '20 • 6\'1" • 190 lbs • R/R',
          stats: '.262 AVG | .327 OBP | .405 SLG | 1 HR | 2 SB',
          summary: 'Corner bat with raw strength but well below-average hit tool. Swing-and-miss issues vs. quality velocity. Limited barrel accuracy leads to inconsistent contact. Fringy defender at 3B, profiles as 1B only. Lacks the hit tool to project as professional hitter.',
          keyStrengths: 'Raw strength; playable arm for 3B',
          mainConcerns: 'Streaky contact; below-average running ability',
          developmentFocus: 'Improve swing decisions; convert raw pop into game power'
        },
        'Michael Butler': {
          scoutingGrades: { hit: 35, power: 35, speed: 45, arm: 40, field: 40 },
          performanceMetrics: { avgExitVelo: 80.4, hardHitRate: 26.9, barrelRate: 4.2, sweetSpotRate: 27.8, maxExitVelo: 98.1 },
          draftRound: '20-30', college: 'NAIA School', position: 'INF',
          overallFV: 30,
          ageSize: '22 • 5\'10" • 180 lbs • R/R',
          stats: '.248 AVG | .306 OBP | .352 SLG | 0 HR | 3 SB',
          summary: 'Fringe prospect with well below-average tools. Lacks bat speed and strength to impact the baseball. No carrying tool. Age (22) and limited ceiling make him organizational filler at best. Unlikely to advance beyond rookie ball.',
          keyStrengths: 'Hard-nosed, competitive at-bats',
          mainConcerns: 'No plus tool; limited offensive upside',
          developmentFocus: 'Emphasize versatility, polish approach for depth role'
        },
        'David Campbell': {
          scoutingGrades: { hit: 45, power: 45, speed: 45, arm: 50, field: 45 },
          performanceMetrics: { avgExitVelo: 87.2, hardHitRate: 38.4, barrelRate: 9.8, sweetSpotRate: 36.2, maxExitVelo: 104.1 },
          draftRound: '10-15', college: 'Division I', position: 'INF',
          overallFV: 40,
          ageSize: '22 • 6\'1" • 195 lbs • R/R',
          stats: '.268 AVG | .339 OBP | .408 SLG | 1 HR | 6 SB',
          summary: 'Sturdy INF with gap-to-gap pop and defensive reliability. Shows raw strength and flashes the ability to impact the ball when on time. Fits as a steady contributor with a chance to climb if bat control improves.',
          keyStrengths: 'Strong frame; flashes of gap power',
          mainConcerns: 'Consistency of contact; limited speed',
          developmentFocus: 'Improve barrel control and timing; polish swing efficiency'
        },
        'Chris Anderson': {
          scoutingGrades: { hit: 40, power: 35, speed: 45, arm: 40, field: 45 },
          performanceMetrics: { avgExitVelo: 82.4, hardHitRate: 29.8, barrelRate: 5.9, sweetSpotRate: 32.4, maxExitVelo: 100.2 },
          draftRound: '15-25', college: 'State University', position: 'OF',
          overallFV: 35,
          ageSize: '21 • 6\'1" • 185 lbs • R/R',
          stats: '.285 AVG | .352 OBP | .398 SLG | 2 HR | 8 SB',
          summary: 'Fringe OF prospect with below-average tools. Lacks impact bat and arm strength for corner OF. Fourth OF ceiling. Organizational depth piece.',
          keyStrengths: 'Decent athlete; Shows some barrel control; Coachable player',
          mainConcerns: 'Lacks present power; Fringe arm for OF; Below-average hit projection',
          developmentFocus: 'Fourth outfielder ceiling. Organizational depth piece. Late-round filler pick.'
        },
        'Sam Wilson': {
          scoutingGrades: { hit: 35, power: 40, speed: 50, arm: 35, field: 40 },
          performanceMetrics: { avgExitVelo: 83.9, hardHitRate: 30.5, barrelRate: 6.2, sweetSpotRate: 32.8, maxExitVelo: 101.7 },
          draftRound: '25-30/UDFA', college: 'Junior College', position: 'OF/2B',
          overallFV: 30,
          ageSize: '20 • 5\'10" • 175 lbs • L/R',
          stats: '.265 AVG | .318 OBP | .345 SLG | 0 HR | 12 SB',
          summary: 'JuCo utility player with below-average tools. Limited upside but shows some versatility and work ethic.',
          keyStrengths: 'Can play multiple positions; Decent work ethic; Shows some speed',
          mainConcerns: 'Well below-average hit tool; Limited power potential; Fringe defensive skills',
          developmentFocus: 'Organizational depth only. Utility role at best. UDFA candidate.'
        },
        'Kevin Martinez': {
          scoutingGrades: { hit: 35, power: 40, speed: 40, arm: 45, field: 40 },
          performanceMetrics: { avgExitVelo: 82.4, hardHitRate: 28.9, barrelRate: 5.8, sweetSpotRate: 29.4, maxExitVelo: 101.2 },
          draftRound: '20-30', college: 'Division II', position: 'C/1B',
          overallFV: 35,
          ageSize: '21 • 6\'0" • 200 lbs • R/R',
          stats: '.278 AVG | .345 OBP | .425 SLG | 3 HR | 1 SB',
          summary: 'D2 catcher with fringe tools but shows some game awareness. Limited offensive upside but decent receiver.',
          keyStrengths: 'Solid receiver; Works well with pitchers; Shows some pop in BP',
          mainConcerns: 'Below-average hit projection; Limited power potential; Fringe arm strength',
          developmentFocus: 'Backup catcher ceiling. Organizational depth piece. Late-round developmental pick.'
        },
        'Luis Garcia': {
          scoutingGrades: { hit: 40, power: 30, speed: 40, arm: 40, field: 45 },
          performanceMetrics: { avgExitVelo: 79.8, hardHitRate: 25.4, barrelRate: 3.9, sweetSpotRate: 33.1, maxExitVelo: 96.2 },
          draftRound: '25-30/UDFA', college: 'Community College', position: '2B/SS',
          overallFV: 35,
          ageSize: '20 • 5\'9" • 170 lbs • R/R',
          stats: '.295 AVG | .358 OBP | .375 SLG | 1 HR | 5 SB',
          summary: 'CC middle infielder with contact-over-power profile. Shows some barrel control but lacks impact tools.',
          keyStrengths: 'Makes consistent contact; Decent infield actions; Good baseball IQ',
          mainConcerns: 'Well below-average power; Limited athletic ability; Fringe arm for SS',
          developmentFocus: 'Utility infielder ceiling. Organizational filler. Late-round depth pick.'
        },
        'Joe Gomes': {
          scoutingGrades: { hit: 40, power: 30, speed: 50, arm: 45, field: 50 },
          performanceMetrics: { avgExitVelo: 83.1, hardHitRate: 29.8, barrelRate: 5.2, sweetSpotRate: 33.6, maxExitVelo: 99.8 },
          draftRound: '18-25', college: 'State University', position: 'OF',
          overallFV: 35,
          ageSize: '21 • 6\'0" • 185 lbs • R/R',
          stats: '.265 AVG | .332 OBP | .375 SLG | 5 SB',
          summary: 'Athletic OF with consistent defense and improving offensive value. Instincts and reads in the outfield project as above-average for the level. Offensively, he competes with line-drive contact and projects as a steady contributor.',
          keyStrengths: 'Defensive reliability; competitive at-bats',
          mainConcerns: 'Modest power ceiling; occasional swing length',
          developmentFocus: 'Add strength, produce more consistent hard contact'
        },
        'Chris Hall': {
          scoutingGrades: { hit: 40, power: 35, speed: 45, arm: 40, field: 40 },
          performanceMetrics: { avgExitVelo: 82.1, hardHitRate: 28.3, barrelRate: 5.2, sweetSpotRate: 32.1, maxExitVelo: 98.7 },
          draftRound: '20/UDFA', college: 'NAIA School', position: 'INF',
          overallFV: 35,
          ageSize: '22 • 5\'11" • 182 lbs • R/R',
          stats: '.241 AVG | .306 OBP | .348 SLG | 2 SB',
          summary: 'Steady infielder without a standout tool. Below-average bat speed caps upside, but effort and feel for the game allow him to contribute as organizational depth.',
          keyStrengths: 'Versatility; baseball IQ',
          mainConcerns: 'Light contact quality; limited ceiling',
          developmentFocus: 'Improve swing decisions to maximize on-base ability'
        },
        'Sam Harrison': {
          scoutingGrades: { hit: 35, power: 30, speed: 45, arm: 45, field: 45 },
          performanceMetrics: { avgExitVelo: 80.4, hardHitRate: 26.2, barrelRate: 4.1, sweetSpotRate: 30.8, maxExitVelo: 96.8 },
          draftRound: '15-20', college: 'Division II', position: 'INF',
          overallFV: 35,
          ageSize: '21 • 6\'1" • 190 lbs • L/R',
          stats: '.272 AVG | .340 OBP | .372 SLG | 3 SB',
          summary: 'Lefty bat with a gap-to-gap approach and solid defensive versatility. Lacks true impact tools but competes and fits as an INF depth piece.',
          keyStrengths: 'Lefty contact bat; steady defender',
          mainConcerns: 'Below-average power; no carrying tool',
          developmentFocus: 'Add strength and leverage to bat path'
        },
        'Kevin Jones': {
          scoutingGrades: { hit: 40, power: 40, speed: 50, arm: 45, field: 45 },
          performanceMetrics: { avgExitVelo: 85.1, hardHitRate: 32.7, barrelRate: 6.8, sweetSpotRate: 33.2, maxExitVelo: 103.4 },
          draftRound: '18-25', college: 'State University', position: 'OF',
          overallFV: 35,
          ageSize: '20 • 6\'2" • 195 lbs • R/R',
          stats: '.258 AVG | .325 OBP | .368 SLG | 4 SB',
          summary: 'Strong-bodied OF with developing pop. Offense is inconsistent, but athleticism and arm strength give him depth upside.',
          keyStrengths: 'Physical frame; flashes raw power',
          mainConcerns: 'Swing/miss vs velo; inconsistent contact',
          developmentFocus: 'Refine approach, improve swing discipline'
        },
        'Luis Jimenez': {
          scoutingGrades: { hit: 40, power: 35, speed: 45, arm: 40, field: 40 },
          performanceMetrics: { avgExitVelo: 82.7, hardHitRate: 28.9, barrelRate: 5.4, sweetSpotRate: 32.8, maxExitVelo: 99.1 },
          draftRound: '20/UDFA', college: 'Community College', position: 'INF',
          overallFV: 35,
          ageSize: '22 • 5\'10" • 180 lbs • R/R',
          stats: '.239 AVG | .305 OBP | .352 SLG | 2 SB',
          summary: 'Undersized infielder with limited offensive impact. Brings effort and energy, projects more as organizational depth.',
          keyStrengths: 'Hustle and makeup; versatile glove',
          mainConcerns: 'Lack of offensive impact; below-average tools',
          developmentFocus: 'Shorten swing, improve contact frequency'
        },
      };
      
      return profiles[hitterName] || {
        scoutingGrades: { hit: 40, power: 40, speed: 40, arm: 40, field: 40 },
        performanceMetrics: { avgExitVelo: 84.0, hardHitRate: 31.0, barrelRate: 6.5, sweetSpotRate: 32.0, maxExitVelo: 101.0 },
        draftRound: '15-20', college: 'State University', position: 'UTIL'
      };
    };

    return getMockHitterProfile(selectedHitter);
  }, [selectedHitter]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* HEADER */}
      <div className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
          <h1 className="text-2xl font-bold tracking-tight text-white">⚡ Hitter Scouting Reports</h1>
          <div className="text-sm text-white/70">
            Professional scouting evaluations and development tracking
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="mx-auto max-w-7xl px-6 pb-6">
        {/* CONTROLS */}
        <section className="mt-3">
          <div className="max-w-xs">
            <label htmlFor="hitterSel" className="block text-xs text-white/60 mb-1">Select Hitter</label>
            <CustomDropdown
              id="hitterSel"
              aria-label="Select hitter"
              value={selectedHitter}
              onChange={(e) => setSelectedHitter(e.target.value)}
              options={mockHitters.map(h => ({ 
                value: h, 
                label: h.includes('Jake Allen') ? `${h} - OF` :
                       h.includes('Tyler Armstrong') ? `${h} - OF` :
                       h.includes('Alex Bell') ? `${h} - INF` :
                       h.includes('Michael Butler') ? `${h} - INF` :
                       h.includes('David Campbell') ? `${h} - Utility` :
                       h.includes('Carlos Cooper') ? `${h} - INF` :
                       h.includes('Ryan Dawson') ? `${h} - INF` :
                       h.includes('Josh Dixon') ? `${h} - OF` :
                       h.includes('Matt Edwards') ? `${h} - INF` :
                       h.includes('Tony Escobar') ? `${h} - OF` :
                       h.includes('Steve Fisher') ? `${h} - OF` :
                       h.includes('Nick Fox') ? `${h} - INF` :
                       h.includes('Brad Gibson') ? `${h} - INF` :
                       h.includes('Joe Gomes') ? `${h} - OF` :
                       h.includes('Chris Hall') ? `${h} - INF` :
                       h.includes('Sam Harrison') ? `${h} - INF` :
                       h.includes('Kevin Jones') ? `${h} - OF` :
                       h.includes('Luis Jimenez') ? `${h} - INF` :
                       `${h} - INF`
              }))}
              className="w-full text-sm"
              placeholder="Select Hitter"
              style={{
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}
            />
          </div>
        </section>

        {/* Main Content Grid */}
        <section className="mt-3">
          <ProfessionalScoutingReport 
            data={hitterData.scoutingGrades} 
            hitter={selectedHitter} 
            performanceData={hitterData.performanceMetrics}
            profile={{
              college: hitterData.college,
              position: hitterData.position,
              draftRound: hitterData.draftRound,
              summary: hitterData.summary,
              keyStrengths: hitterData.keyStrengths,
              mainConcerns: hitterData.mainConcerns,
              developmentFocus: hitterData.developmentFocus,
              overallFV: hitterData.overallFV,
              ageSize: hitterData.ageSize,
              stats: hitterData.stats
            }}
          />
        </section>

      </main>
    </div>
  );
}
